import type { InfiniteData } from '@tanstack/react-query';
import { useEffect } from 'react';

import { ICustomMessage, messageAttributesSchema } from '@/components/chat';
import { InquiryParticipant } from '@/hooks/useChat';
import type { ChatConversation, ChatMessage } from '@/services/chat/chatTransport';
import { queryClient } from '@/util/queryClient';
import { Sentry } from '@/util/sentry';

type ChatPageData = {
  hasPreviousPage: boolean;
  messages: ICustomMessage[];
};

type UseChatRealtimeSyncParams = {
  conversation: ChatConversation;
  closedConversation: boolean;
  recipient: InquiryParticipant;
  parseMessage: (message: ChatMessage, recipient: InquiryParticipant) => ICustomMessage;
  scrollToTop: () => void;
  debugChatMessages: boolean;
};

export function useChatRealtimeSync({
  conversation,
  closedConversation,
  recipient,
  parseMessage,
  scrollToTop,
  debugChatMessages,
}: UseChatRealtimeSyncParams) {
  useEffect(() => {
    if (closedConversation) {
      return;
    }

    const onMessageAdded = async (message: ChatMessage) => {
      const parsedMessage = parseMessage(message, recipient);

      if (debugChatMessages) {
        const parseResult = messageAttributesSchema.safeParse(message.attributes);

        Sentry.captureMessage('Debug: chat message attributes', {
          level: 'info',
          tags: { context: 'debug_chat_messages', conversationSid: conversation.id },
          extra: {
            messageSid: message.id,
            author: message.author,
            rawAttributes: message.attributes,
            parsedAttributes: parseResult.success ? parseResult.data : null,
            parseError: parseResult.success ? null : parseResult.error.message,
          },
        });
      }

      queryClient.setQueryData<InfiniteData<ChatPageData>>(['chat', conversation.id], (old) => {
        if (!old || old.pages.length === 0) {
          return old;
        }

        const firstPage = old.pages[0];

        if (!firstPage) {
          return old;
        }

        return {
          ...old,
          pages: [
            {
              ...firstPage,
              messages: [parsedMessage, ...firstPage.messages],
            },
            ...old.pages.slice(1),
          ],
        };
      });

      const isFromRecipient = Number(message.author) === recipient.id;

      const hiddenDraftForRecipient = parsedMessage.draft && isFromRecipient;

      if (!hiddenDraftForRecipient) {
        scrollToTop();
      }
    };

    const onMessageUpdated = (messageUpdated: ChatMessage) => {
      const rawAttributes =
        messageUpdated.attributes && typeof messageUpdated.attributes === 'object'
          ? (messageUpdated.attributes as Record<string, unknown>)
          : undefined;

      const normalizedAttributes =
        rawAttributes?.data && typeof rawAttributes.data === 'object'
          ? { ...rawAttributes, ...(rawAttributes.data as Record<string, unknown>) }
          : rawAttributes;

      const updateResult = messageAttributesSchema.safeParse(normalizedAttributes);

      const data = updateResult.success ? updateResult.data : undefined;

      const isFromRecipient = Number(messageUpdated.author) === recipient.id;

      let undraftedForRecipient = false;

      queryClient.setQueryData<InfiniteData<ChatPageData>>(['chat', conversation.id], (old) => {
        if (!old || old.pages.length === 0) {
          return old;
        }

        return {
          ...old,
          pages: old.pages.map((page) => {
            return {
              ...page,
              messages: page.messages.map((message) => {
                if (message.messageId !== messageUpdated.id) {
                  return message;
                }

                if (!data) {
                  return message;
                }

                const wasDraft = Boolean(message.draft);

                const isDraft = Boolean(data.draft);

                const justUndrafted = wasDraft && !isDraft;

                if (justUndrafted && isFromRecipient) {
                  undraftedForRecipient = true;
                }

                return {
                  ...message,
                  files: data.files,
                  type: data.type,
                  draft: data.draft,
                  source: data.source,
                  reference: data.reference,
                  extractedQuestions: data.extractedQuestions,
                  primaryQuestionIndex: data.primaryQuestionIndex,
                  approved: data.approved,
                  publicationShares: data.publicationShares,
                  processingStatus: data.pipelineStatus,
                  processingStatusAt: data.pipelineStatusAt,
                  createdAt:
                    justUndrafted && isFromRecipient
                      ? messageUpdated.updatedAt || new Date()
                      : message.createdAt,
                };
              }),
            };
          }),
        };
      });

      if (undraftedForRecipient) {
        scrollToTop();
      }
    };

    const stopAdded = conversation.onMessageAdded(onMessageAdded);

    const stopUpdated = conversation.onMessageUpdated(onMessageUpdated);

    return () => {
      stopAdded();

      stopUpdated();
    };
  }, [closedConversation, conversation, parseMessage, recipient, scrollToTop, debugChatMessages]);
}
